(() => {
  const theme = window.theme || { routes: {}, strings: {} };
  const routes = theme.routes;
  const strings = theme.strings;

  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  /* ---------- Overlays (mobile nav, cart drawer, search drawer) ---------- */

  const openOverlays = new Set();

  function lockScroll() {
    document.body.classList.toggle('is-locked', openOverlays.size > 0);
  }

  function openOverlay(el, focusTarget) {
    if (!el) return;
    el.hidden = false;
    requestAnimationFrame(() => el.classList.add('is-open'));
    openOverlays.add(el);
    lockScroll();
    const target = focusTarget || el.querySelector('input, button, a');
    if (target) setTimeout(() => target.focus(), 50);
  }

  function closeOverlay(el) {
    if (!el || el.hidden) return;
    el.classList.remove('is-open');
    el.hidden = true;
    openOverlays.delete(el);
    lockScroll();
  }

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    openOverlays.forEach((el) => closeOverlay(el));
  });

  /* ---------- Mobile navigation ---------- */

  const mobileNav = $('[data-mobile-nav]');
  $$('[data-menu-open]').forEach((btn) =>
    btn.addEventListener('click', () => {
      openOverlay(mobileNav);
      btn.setAttribute('aria-expanded', 'true');
    }),
  );
  $$('[data-menu-close]').forEach((btn) =>
    btn.addEventListener('click', () => {
      closeOverlay(mobileNav);
      $$('[data-menu-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
    }),
  );

  /* ---------- Cart ---------- */

  const cartDrawer = () => $('[data-cart-drawer]');
  const cartSections = ['cart-drawer', 'cart-icon-bubble'];

  function renderCartSections(sections) {
    if (!sections) return;
    const parser = new DOMParser();

    if (sections['cart-drawer']) {
      const doc = parser.parseFromString(sections['cart-drawer'], 'text/html');
      const next = doc.querySelector('[data-cart-drawer-panel]');
      const current = $('[data-cart-drawer-panel]');
      if (next && current) current.innerHTML = next.innerHTML;
    }

    if (sections['cart-icon-bubble']) {
      const doc = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
      const bubble = $('#cart-icon-bubble');
      const source = doc.querySelector('.shopify-section') || doc.body;
      if (bubble) bubble.innerHTML = source.innerHTML;
    }
  }

  async function cartRequest(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { Accept: 'application/json', ...(body instanceof FormData ? {} : { 'Content-Type': 'application/json' }) },
      body: body instanceof FormData ? body : JSON.stringify(body),
    });
    const data = await response.json();
    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || strings.cartError);
    }
    return data;
  }

  function openCart() {
    openOverlay(cartDrawer(), $('[data-cart-close]', cartDrawer()));
  }

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-cart-open]');
    if (opener && cartDrawer()) {
      event.preventDefault();
      openCart();
      return;
    }

    if (event.target.closest('[data-cart-close]')) {
      closeOverlay(cartDrawer());
      return;
    }

    const change = event.target.closest('[data-cart-change]');
    if (change) {
      event.preventDefault();
      const drawer = cartDrawer();
      drawer.classList.add('is-busy');
      cartRequest(`${routes.cartChange}.js`, {
        line: Number(change.dataset.line),
        quantity: Math.max(0, Number(change.dataset.quantity)),
        sections: cartSections,
        sections_url: window.location.pathname,
      })
        .then((cart) => {
          renderCartSections(cart.sections);
          if (document.body.classList.contains('template-cart')) window.location.reload();
        })
        .catch((error) => {
          const box = $('[data-cart-errors]', drawer);
          if (box) {
            box.textContent = error.message;
            box.hidden = false;
          }
        })
        .finally(() => drawer.classList.remove('is-busy'));
    }
  });

  /* ---------- Product page ---------- */

  function initProduct(root) {
    const jsonEl = $('[data-product-json]', root);
    if (!jsonEl) return;
    const { variants } = JSON.parse(jsonEl.textContent);
    const form = $('#ProductForm', root);
    const idInput = $('[data-variant-input]', root);
    const addButton = $('[data-add-to-cart]', root);
    const errorBox = $('[data-product-error]', root);
    const optionGroups = $$('[data-option-index]', root);

    const selected = optionGroups.map((group) => {
      const active = $('.product-variant.active', group);
      return active ? active.dataset.optionValue : null;
    });

    function showMedia(id) {
      if (!id) return;
      const key = String(id);
      const target = $(`[data-media="${key}"]`, root);
      if (!target) return;
      $$('[data-media]', root).forEach((el) => {
        const isTarget = el === target;
        el.hidden = !isTarget;
        if (!isTarget) $$('video', el).forEach((v) => v.pause());
      });
      $$('[data-media-thumb]', root).forEach((thumb) =>
        thumb.classList.toggle('active', thumb.dataset.mediaThumb === key),
      );
    }

    $$('[data-media-thumb]', root).forEach((thumb) =>
      thumb.addEventListener('click', () => showMedia(thumb.dataset.mediaThumb)),
    );

    const thumbs = $('.product-gallery-thumbs', root);
    const thumbsWrap = $('.product-gallery-thumbs-wrap', root);
    const desktopThumbs = window.matchMedia('(min-width: 901px)');

    function updateThumbFades() {
      if (!thumbs || !thumbsWrap) return;
      if (!desktopThumbs.matches) {
        thumbsWrap.classList.remove('has-fade-top', 'has-fade-bottom');
        return;
      }
      const { scrollTop, clientHeight, scrollHeight } = thumbs;
      thumbsWrap.classList.toggle('has-fade-top', scrollTop > 1);
      thumbsWrap.classList.toggle('has-fade-bottom', scrollTop + clientHeight < scrollHeight - 1);
    }

    if (thumbs && thumbsWrap) {
      thumbs.addEventListener('scroll', updateThumbFades, { passive: true });
      window.addEventListener('resize', updateThumbFades);
      desktopThumbs.addEventListener('change', updateThumbFades);
      $$('img', thumbs).forEach((img) => {
        if (!img.complete) img.addEventListener('load', updateThumbFades, { once: true });
      });
      requestAnimationFrame(updateThumbFades);
    }

    function stockLabel(variant) {
      if (!variant || !variant.available) return strings.unavailable;
      if (variant.stock != null && variant.stock > 0 && variant.stock <= 5) {
        return strings.inStockCount.replace('__COUNT__', variant.stock);
      }
      return strings.inStock;
    }

    function update() {
      const variant = variants.find((v) => v.options.every((value, i) => value === selected[i]));

      optionGroups.forEach((group, index) => {
        $('[data-option-selected]', group).textContent = selected[index] || '';
        $$('[data-option-value]', group).forEach((btn) => {
          const isActive = btn.dataset.optionValue === selected[index];
          btn.classList.toggle('active', isActive);
          btn.setAttribute('aria-pressed', String(isActive));
          const candidate = variants.find((v) =>
            v.options.every((value, i) => (i === index ? value === btn.dataset.optionValue : value === selected[i])),
          );
          btn.classList.toggle('is-unavailable', !candidate || !candidate.available);
        });
      });

      $('[data-price-current]', root).textContent = variant ? variant.price : '';
      let compare = $('[data-price-compare]', root);
      if (variant && variant.compare) {
        if (!compare) {
          compare = document.createElement('s');
          compare.className = 'product-compare-price';
          compare.dataset.priceCompare = '';
          $('[data-price]', root).appendChild(compare);
        }
        compare.textContent = variant.compare;
      } else if (compare) {
        compare.remove();
      }

      const lowest = $('[data-lowest-price]', root);
      if (lowest) {
        lowest.hidden = !(variant && variant.lowest);
        $('[data-lowest-price-value]', lowest).textContent = variant && variant.lowest ? variant.lowest : '';
      }

      $('[data-stock]', root).textContent = stockLabel(variant);

      const details = $('[data-variant-details]', root);
      if (details && variant) details.innerHTML = variant.details;

      const available = Boolean(variant && variant.available);
      addButton.disabled = !available;
      addButton.textContent = available ? strings.addToCart : strings.unavailable;
      if (errorBox) errorBox.hidden = true;

      if (variant) {
        idInput.value = variant.id;
        showMedia(variant.media);
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    optionGroups.forEach((group, index) => {
      $$('[data-option-value]', group).forEach((btn) =>
        btn.addEventListener('click', () => {
          selected[index] = btn.dataset.optionValue;
          update();
        }),
      );
    });

    if (form) {
      form.addEventListener('submit', (event) => {
        if (!cartDrawer()) return;
        event.preventDefault();
        const body = new FormData(form);
        body.append('sections', cartSections.join(','));
        body.append('sections_url', window.location.pathname);
        addButton.disabled = true;
        cartRequest(`${routes.cartAdd}.js`, body)
          .then((item) => {
            renderCartSections(item.sections);
            openCart();
          })
          .catch((error) => {
            if (errorBox) {
              errorBox.textContent = error.message;
              errorBox.hidden = false;
            }
          })
          .finally(() => {
            addButton.disabled = false;
          });
      });
    }
  }

  $$('[data-product]').forEach(initProduct);

  /* ---------- Background videos ---------- */

  $$('.story-video__media').forEach((video) => {
    if (video.tagName !== 'VIDEO') return;
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    const play = () => video.play().catch(() => {});
    if (video.readyState >= 2) play();
    else video.addEventListener('loadeddata', play, { once: true });
  });

  /* ---------- Rotating quotes ---------- */

  $$('[data-story-quotes]').forEach((section) => {
    const quotes = $$('[data-quote]', section);
    const dots = $$('[data-quote-dot]', section);
    if (quotes.length < 2) return;
    let index = 0;
    const show = (next) => {
      index = next;
      quotes.forEach((q, i) => {
        q.hidden = i !== index;
      });
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
        dot.setAttribute('aria-selected', String(i === index));
      });
    };
    let timer = setInterval(() => show((index + 1) % quotes.length), Number(section.dataset.interval) || 4500);
    dots.forEach((dot, i) =>
      dot.addEventListener('click', () => {
        clearInterval(timer);
        show(i);
        timer = setInterval(() => show((index + 1) % quotes.length), Number(section.dataset.interval) || 4500);
      }),
    );
  });

  /* ---------- Catalog toolbar ---------- */

  $$('[data-catalog-form]').forEach((form) => {
    $$('[data-catalog-auto]', form).forEach((input) => input.addEventListener('change', () => form.submit()));
  });

  /* ---------- Search drawer + predictive search ---------- */

  const searchDrawer = $('[data-search-drawer]');
  if (searchDrawer) {
    const input = $('[data-search-input]', searchDrawer);
    const results = $('[data-search-results]', searchDrawer);
    const defaultList = $('[data-search-default]', searchDrawer);
    const allLink = $('[data-search-all]', searchDrawer);
    const allHref = allLink ? allLink.getAttribute('href') : '/collections/all';
    let controller;
    let timer;

    $$('[data-search-open]').forEach((btn) => btn.addEventListener('click', () => openOverlay(searchDrawer, input)));
    $$('[data-search-close]', searchDrawer).forEach((btn) => btn.addEventListener('click', () => closeOverlay(searchDrawer)));

    const renderDefault = () => {
      $$('[data-predictive-results]', results).forEach((el) => el.remove());
      defaultList.hidden = false;
      if (allLink) allLink.setAttribute('href', allHref);
    };

    input.addEventListener('input', () => {
      clearTimeout(timer);
      const q = input.value.trim();
      if (!q) {
        renderDefault();
        return;
      }
      timer = setTimeout(async () => {
        if (controller) controller.abort();
        controller = new AbortController();
        const params = new URLSearchParams({
          q,
          section_id: 'predictive-search',
          'resources[type]': 'product',
          'resources[limit]': '12',
        });
        try {
          const response = await fetch(`${routes.predictiveSearch}?${params}`, { signal: controller.signal });
          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.querySelector('[data-predictive-results]');
          $$('[data-predictive-results]', results).forEach((el) => el.remove());
          if (fresh) {
            defaultList.hidden = true;
            results.appendChild(fresh);
          }
          if (allLink) allLink.setAttribute('href', `/search?type=product&q=${encodeURIComponent(q)}`);
        } catch (error) {
          if (error.name !== 'AbortError') renderDefault();
        }
      }, 200);
    });
  }

  /* ---------- Cookie settings (Shopify cookie banner) ---------- */

  $$('[data-cookie-settings]').forEach((btn) =>
    btn.addEventListener('click', () => {
      if (window.privacyBanner && typeof window.privacyBanner.showPreferences === 'function') {
        window.privacyBanner.showPreferences();
      } else {
        window.location.href = '/policies/privacy-policy';
      }
    }),
  );

  /* ---------- Withdrawal form (two-step confirmation) ---------- */

  const withdrawal = $('#WithdrawalForm');
  if (withdrawal) {
    const storageKey = 'nossa-withdrawal-confirmation';
    const step1 = $('[data-withdrawal-step="1"]', withdrawal);
    const step2 = $('[data-withdrawal-step="2"]', withdrawal);
    const summary = $('[data-withdrawal-summary]', withdrawal);
    const stamp = $('[data-withdrawal-timestamp]', withdrawal);
    const success = $('[data-withdrawal-success]', withdrawal);
    const downloadButton = $('[data-withdrawal-download]', withdrawal);
    let confirmed = false;

    function getStoredConfirmation() {
      try {
        return JSON.parse(sessionStorage.getItem(storageKey));
      } catch {
        return null;
      }
    }

    function downloadConfirmation() {
      const payload = getStoredConfirmation();
      if (!payload) return;
      const lines = [
        'POTWIERDZENIE OTRZYMANIA OŚWIADCZENIA O ODSTĄPIENIU OD UMOWY',
        '',
        'Sprzedawca: PACK SERVICE BARAŃSKI SPÓŁKA KOMANDYTOWA',
        'Baziowa 28, 43-100 Tychy, Polska',
        'info@nossapadel.pl',
        '',
        `Data i godzina otrzymania: ${payload.submittedAt}`,
        '',
        'Oświadczenie:',
        'Niniejszym informuję o moim odstąpieniu od umowy sprzedaży wskazanych poniżej produktów.',
        '',
        ...payload.fields.flatMap(({ label, value }) => [`${label}:`, value, '']),
      ];
      const blob = new Blob([`\uFEFF${lines.join('\r\n')}`], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `potwierdzenie-odstapienia-${payload.fileStamp}.txt`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }

    if (success && downloadButton) {
      downloadButton.addEventListener('click', downloadConfirmation);
      if (getStoredConfirmation()) setTimeout(downloadConfirmation, 300);
    }

    if (step1 && step2) {
      withdrawal.addEventListener('submit', (event) => {
        if (confirmed) {
          const now = new Date();
          stamp.value = now.toLocaleString('pl-PL', { timeZone: 'Europe/Warsaw' });
          const fields = $$('[data-summary-label]', step1)
            .filter((field) => field.value.trim())
            .map((field) => ({ label: field.dataset.summaryLabel, value: field.value.trim() }));
          sessionStorage.setItem(
            storageKey,
            JSON.stringify({
              submittedAt: stamp.value,
              fileStamp: now.toISOString().replace(/[:.]/g, '-'),
              fields,
            }),
          );
          return;
        }
        event.preventDefault();
        if (!withdrawal.reportValidity()) return;
        summary.innerHTML = '';
        $$('[data-summary-label]', step1).forEach((field) => {
          if (!field.value.trim()) return;
          const dt = document.createElement('dt');
          dt.textContent = field.dataset.summaryLabel;
          const dd = document.createElement('dd');
          dd.textContent = field.value.trim();
          summary.append(dt, dd);
        });
        step1.hidden = true;
        step2.hidden = false;
        step2.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });

      $('[data-withdrawal-confirm]', step2).addEventListener('click', () => {
        confirmed = true;
      });
      $('[data-withdrawal-back]', step2).addEventListener('click', () => {
        step2.hidden = true;
        step1.hidden = false;
      });
    }
  }
})();
